import React, { useState } from 'react';
import { Heart, X, Check, ArrowLeft, Link2, Mail, Copy, Share2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ColorImage } from '../../admin/fabric/ColorImage';
import { roomLabel } from '../../../lib/roomTypes';
import { ShortlistQuoteDialog } from './ShortlistQuoteDialog';
import { brandLabel } from '../../../lib/brandNames';

const Row = ({ label, children }) => (
  <div className="py-2 border-t border-neutral-100">
    <p className="text-[10px] uppercase tracking-wider text-neutral-400">{label}</p>
    <div className="text-sm text-neutral-800 mt-0.5 min-h-[1.25rem]">{children || <span className="text-neutral-300">—</span>}</div>
  </div>
);

export const buildShortlistLink = (entries) =>
  `${window.location.origin}/fabrics?shortlist=${entries.map((e) => e.id).join(',')}`;

const ShareBar = ({ favorites }) => {
  const [copied, setCopied] = useState(false);
  const link = buildShortlistLink(favorites);
  const names = favorites.map((f) => `${f.name} (${brandLabel(f.brand_name)} · ${f.collection_name})`).join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Shortlist link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link', link);
    }
  };

  const mailto = `mailto:?subject=${encodeURIComponent('My Apollo Shades fabric shortlist')}&body=${encodeURIComponent(
    `Have a look at the fabrics I'm considering:\n\n${names}\n\nView them side by side here:\n${link}`
  )}`;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-neutral-50 px-4 py-3" data-testid="shortlist-share-bar">
      <Share2 className="w-4 h-4 text-neutral-500" />
      <span className="text-sm text-neutral-600 mr-2">Share this shortlist</span>
      <Button size="sm" variant="outline" onClick={copy} className="h-8 gap-1.5" data-testid="shortlist-copy-link-btn">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy link'}
      </Button>
      <Button asChild size="sm" variant="outline" className="h-8 gap-1.5">
        <a href={mailto} data-testid="shortlist-email-btn"><Mail className="w-3.5 h-3.5" /> Email</a>
      </Button>
      <span className="text-[11px] text-neutral-400 truncate max-w-xs hidden md:inline-flex items-center gap-1" data-testid="shortlist-share-link">
        <Link2 className="w-3 h-3" /> {link}
      </span>
    </div>
  );
};

// Side-by-side comparison of hearted (or shared) fabrics.
export const FabricShortlistView = ({ favorites, onRemove, onSelect, selectLabel, onBack, shared = false, onSaveShared, roomType }) => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  return (
  <div className="space-y-4" data-testid="fabric-shortlist-view">
    <ShortlistQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} fabrics={favorites} />
    <div className="flex items-center justify-between gap-3">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 -ml-2" data-testid="fabric-shortlist-back">
        <ArrowLeft className="w-4 h-4" /> Back to all fabrics
      </Button>
      <p className="text-sm text-neutral-500" data-testid="fabric-shortlist-count">
        {favorites.length} {shared ? 'in this shared shortlist' : 'in your shortlist'}
      </p>
    </div>

    {shared && favorites.length > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="shortlist-shared-banner">
        <p className="text-sm text-amber-900">Someone shared these fabrics with you.</p>
        <Button size="sm" onClick={onSaveShared} className="h-8 gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="shortlist-save-shared-btn">
          <Heart className="w-3.5 h-3.5" /> Save to my shortlist
        </Button>
      </div>
    )}
    {favorites.length > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-900 bg-neutral-900 text-white px-4 py-3" data-testid="shortlist-quote-bar">
        <div>
          <p className="text-sm font-medium">Ready to price these fabrics?</p>
          <p className="text-xs text-neutral-300">Send the shortlist to our design team — no configuration needed.</p>
        </div>
        <Button size="sm" onClick={() => setQuoteOpen(true)} className="h-9 gap-1.5 bg-amber-500 hover:bg-amber-600 text-neutral-900" data-testid="shortlist-request-quote-btn">
          <FileText className="w-4 h-4" /> Request a quote for these fabrics
        </Button>
      </div>
    )}
    {!shared && favorites.length > 0 && <ShareBar favorites={favorites} />}

    {favorites.length === 0 ? (
      <div className="text-center py-16 text-neutral-500" data-testid="fabric-shortlist-empty">
        <Heart className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
        Tap the heart on any fabric to add it to your shortlist and compare side by side.
      </div>
    ) : (
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-4 min-w-min">
          {favorites.map((entry) => {
            const roomUrl = (roomType && entry.room_visualizations?.[roomType]) || entry.room_visualization_url || entry.ai_visualization_url;
            return (
              <div key={entry.id} className="w-56 shrink-0 rounded-xl border bg-white overflow-hidden" data-testid={`shortlist-item-${entry.id}`}>
                <div className="relative aspect-square">
                  <ColorImage src={entry.image_url || roomUrl} alt={entry.name} hex={entry.hex_color} className="w-full h-full" eager />
                  {!shared && onRemove && (
                    <button
                      type="button"
                      onClick={() => onRemove(entry)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-white shadow"
                      title="Remove from shortlist"
                      data-testid={`shortlist-remove-${entry.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {roomUrl && (
                  <div className="aspect-[4/3] border-t relative">
                    <ColorImage src={roomUrl} alt={`${entry.name} in a room`} hex={entry.hex_color} className="w-full h-full" />
                    {roomType && (
                      <span className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white">
                        {roomLabel(entry.room_visualizations?.[roomType] ? roomType : entry.visualization_room_type)}
                      </span>
                    )}
                  </div>
                )}
                <div className="p-3">
                  <p className="font-medium text-sm truncate" title={entry.name}>{entry.name}</p>
                  <Row label="Brand / Collection">{brandLabel(entry.brand_name)} · {entry.collection_name}{entry.material_name ? ` · ${entry.material_name}` : ''}</Row>
                  <Row label="Type">{entry.fabric_type && <Badge variant="secondary" className="text-[11px]">{entry.fabric_type}</Badge>}</Row>
                  <Row label="Openness">{entry.openness}</Row>
                  <Row label="Code / Hex">
                    <span className="font-mono text-xs">{[entry.code, entry.hex_color].filter(Boolean).join(' · ')}</span>
                  </Row>
                  {onSelect && (
                    <Button size="sm" onClick={() => onSelect(entry)} className="w-full mt-3 gap-1.5 bg-neutral-900 hover:bg-neutral-800" data-testid={`shortlist-select-${entry.id}`}>
                      <Check className="w-3.5 h-3.5" /> {selectLabel}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
  );
};

export default FabricShortlistView;
